import { motion, useReducedMotion } from "motion/react";
import { cn } from "@eduviaos/ui-kit";
import { ziggySrc, type ZiggyAge, type ZiggyPose, type ZiggyVariant } from "../lib/ziggy";
import { useZiggyVariant } from "../lib/ziggyContext";

/**
 * Ziggy the mascot. Renders the chosen look × age × pose art with an optional
 * tenant-tinted glow ring (re-tints with `--color-primary`, hybrid identity
 * per the design spec) and a gentle idle float. The look comes from the
 * learner's saved preference (context) unless a `variant` override is passed
 * (e.g. the first-run chooser previews both). Motion is transform/opacity
 * only and disabled under `prefers-reduced-motion`.
 */
export function Ziggy({
  pose = "happy",
  age = "teen",
  variant,
  size = 96,
  glow = true,
  float = true,
  className,
  alt = "Ziggy, your study buddy",
  priority = false,
}: {
  pose?: ZiggyPose;
  age?: ZiggyAge;
  /** Override the learner's chosen look (defaults to context). */
  variant?: ZiggyVariant;
  /** Rendered height of the character in pixels. */
  size?: number;
  glow?: boolean;
  float?: boolean;
  className?: string;
  /** Decorative Ziggy (paired with visible copy) → pass "". */
  alt?: string;
  priority?: boolean;
}) {
  const reduce = useReducedMotion();
  const { variant: chosen } = useZiggyVariant();
  const look = variant ?? chosen;
  const animate = !reduce && float;

  return (
    <div
      className={cn(
        "pointer-events-none relative grid place-items-end justify-items-center",
        className,
      )}
      style={{ height: size }}
    >
      {glow && (
        <span
          aria-hidden
          className="absolute bottom-1 left-1/2 -translate-x-1/2 rounded-full blur-2xl"
          style={{
            width: size * 0.7,
            height: size * 0.28,
            background:
              "radial-gradient(closest-side, color-mix(in oklch, var(--color-primary) 55%, transparent), transparent)",
          }}
        />
      )}
      <motion.img
        src={ziggySrc(look, age, pose)}
        alt={alt}
        aria-hidden={alt === ""}
        loading={priority ? "eager" : "lazy"}
        draggable={false}
        style={{ height: size }}
        className="relative z-10 w-auto object-contain drop-shadow-[0_12px_20px_rgba(30,20,60,0.28)]"
        animate={
          animate
            ? { transform: ["translateY(0px)", "translateY(-6px)", "translateY(0px)"] }
            : undefined
        }
        transition={animate ? { duration: 4, repeat: Infinity, ease: "easeInOut" } : undefined}
      />
    </div>
  );
}
